import {
    Avatar,
    Box,
    Button,
    Chip,
    IconButton,
    InputAdornment,
    Tab,
    Tabs,
    TextField,
    ToggleButton,
    ToggleButtonGroup,
    Tooltip,
    Typography,
} from "@mui/material";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CancelIcon from "@mui/icons-material/Cancel";
import ArchiveIcon from "@mui/icons-material/Archive";
import VisibilityIcon from "@mui/icons-material/Visibility";
import ParkIcon from "@mui/icons-material/Park";
import HikingIcon from "@mui/icons-material/Hiking";
import SearchIcon from "@mui/icons-material/Search";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "react-toastify";
import Swal from "sweetalert2";
import AxiosInstance from "../../Config/AxiosInstance";
import { ApiList } from "../../Config/ApiList";
import TableContainer from "../../Components/TableContainer";
import SpotDetailDrawer from "./SpotDetailDrawer";
import DummyUser from "../../Assets/User.webp";

const STATUS_TABS = [
    { key: "pending", label: "Pending" },
    { key: "approved", label: "Approved" },
    { key: "rejected", label: "Rejected" },
    { key: "archived", label: "Archived" },
];

const SpotsModeration = () => {
    const [status, setStatus] = useState("pending");
    const [typeFilter, setTypeFilter] = useState("all");
    const [loading, setLoading] = useState(true);
    const [spots, setSpots] = useState([]);
    const [counts, setCounts] = useState({ pending: 0, approved: 0, rejected: 0, archived: 0 });
    const [drawerSpotId, setDrawerSpotId] = useState(null);

    // Search box value is kept separate from the debounced query that
    // actually hits the API, so we don't fetch on every keystroke.
    const [search, setSearch] = useState("");
    const [query, setQuery] = useState("");
    const [total, setTotal] = useState(0);

    const pageSize = 25;
    const [page, setPage] = useState(0);

    useEffect(() => {
        const t = setTimeout(() => {
            setQuery(search.trim());
            setPage(0);
        }, 350);
        return () => clearTimeout(t);
    }, [search]);

    const fetchList = useCallback(async () => {
        setLoading(true);
        try {
            const params = { status, limit: pageSize, offset: page * pageSize };
            if (typeFilter !== "all") params.type = typeFilter;
            if (query) params.q = query;

            const response = await AxiosInstance.get(ApiList.SPOT_ADMIN_LIST, { params });
            if (response?.data?.s === 1) {
                const rows = (response.data.r.spots || []).map((s, i) => ({
                    ...s,
                    rowid: i + 1 + page * pageSize,
                }));
                setSpots(rows);
                setCounts(response.data.r.status_counts || counts);
                setTotal(response.data.r.total ?? rows.length);
            } else {
                setSpots([]);
                toast.error(response?.data?.m || "Failed to load spots");
            }
        } catch (err) {
            console.error("Fetch spots failed:", err);
            toast.error("Failed to load spots");
            setSpots([]);
        } finally {
            setLoading(false);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [status, typeFilter, page, query]);

    useEffect(() => {
        fetchList();
    }, [fetchList]);

    const handleTabChange = (_e, v) => {
        setStatus(v);
        setPage(0);
    };
    const handleTypeChange = (_e, v) => {
        if (v !== null) {
            setTypeFilter(v);
            setPage(0);
        }
    };

    const approveSpot = async (spot) => {
        const result = await Swal.fire({
            title: "Approve this spot?",
            text: `"${spot.name}" will go live on TreatWalk and the submitter gets a push notification.`,
            icon: "question",
            showCancelButton: true,
            confirmButtonColor: "#c6ff00",
            cancelButtonColor: "#888",
            confirmButtonText: "Approve",
        });
        if (!result.isConfirmed) return;
        try {
            const res = await AxiosInstance.post(`${ApiList.SPOT_ADMIN_APPROVE}/${spot.id}/approve`);
            if (res?.data?.s === 1) {
                toast.success("Spot approved");
                fetchList();
            } else {
                toast.error(res?.data?.m || "Approve failed");
            }
        } catch (err) {
            toast.error("Approve failed");
        }
    };

    const rejectSpot = async (spot) => {
        const { value: reason } = await Swal.fire({
            title: `Reject "${spot.name}"?`,
            input: "textarea",
            inputLabel: "Reason (shown to admin only)",
            inputPlaceholder: "e.g. Duplicate, bad location, spam...",
            inputAttributes: { maxlength: 255, rows: 3 },
            inputValidator: (v) => (!v || v.trim().length < 2 ? "Please provide a reason" : null),
            showCancelButton: true,
            confirmButtonColor: "#d33",
            confirmButtonText: "Reject",
        });
        if (!reason) return;
        try {
            const res = await AxiosInstance.post(
                `${ApiList.SPOT_ADMIN_REJECT}/${spot.id}/reject`,
                { reason: reason.trim() }
            );
            if (res?.data?.s === 1) {
                toast.success("Spot rejected");
                fetchList();
            } else {
                toast.error(res?.data?.m || "Reject failed");
            }
        } catch (err) {
            toast.error("Reject failed");
        }
    };

    const archiveSpot = async (spot) => {
        const result = await Swal.fire({
            title: "Archive this spot?",
            text: `"${spot.name}" will be hidden from the app. You can restore it by editing the row later.`,
            icon: "warning",
            showCancelButton: true,
            confirmButtonColor: "#d33",
            confirmButtonText: "Archive",
        });
        if (!result.isConfirmed) return;
        try {
            const res = await AxiosInstance.post(`${ApiList.SPOT_ADMIN_ARCHIVE}/${spot.id}/archive`);
            if (res?.data?.s === 1) {
                toast.success("Spot archived");
                fetchList();
            } else {
                toast.error(res?.data?.m || "Archive failed");
            }
        } catch (err) {
            toast.error("Archive failed");
        }
    };

    const columns = useMemo(() => [
        {
            field: "rowid",
            headerName: "#",
            width: 60,
            sortable: false,
        },
        {
            field: "thumbnail_url",
            headerName: "",
            width: 60,
            sortable: false,
            renderCell: (p) => (
                <Avatar
                    variant="rounded"
                    src={p.value ? `${ApiList.IMG_URL}${p.value}` : DummyUser}
                    sx={{ width: 40, height: 40 }}
                />
            ),
        },
        {
            field: "name",
            headerName: "Name",
            flex: 1.3,
            minWidth: 200,
            renderCell: (p) => (
                <Box sx={{ py: 0.5 }}>
                    <Typography sx={{ fontSize: 14, fontWeight: 600, color: "#000" }}>
                        {p.row.name}
                    </Typography>
                    <Typography sx={{ fontSize: 11, color: "#666" }}>
                        #{p.row.id} · {new Date(p.row.created_at).toLocaleDateString()}
                    </Typography>
                </Box>
            ),
        },
        {
            field: "type",
            headerName: "Type",
            width: 110,
            renderCell: (p) => (
                <Chip
                    size="small"
                    icon={p.value === "park" ? <ParkIcon /> : <HikingIcon />}
                    label={p.value === "park" ? "Park" : "Trail"}
                    sx={{ background: "#b9cc0360", color: "#000", fontWeight: 500 }}
                />
            ),
        },
        {
            field: "submitter",
            headerName: "Submitter",
            flex: 0.9,
            minWidth: 150,
            renderCell: (p) => (
                <Typography sx={{ fontSize: 13, color: "#000" }}>
                    {p.row.submitter_username || p.row.submitter_fname || p.row.submitter_email || "—"}
                </Typography>
            ),
        },
        {
            field: "tags",
            headerName: "Tags",
            flex: 1.3,
            minWidth: 180,
            sortable: false,
            renderCell: (p) => (
                <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5, py: 0.5 }}>
                    {(p.row.tags || []).slice(0, 4).map((t) => (
                        <Chip
                            key={t.id}
                            size="small"
                            label={t.name}
                            sx={{ fontSize: 10, height: 20, background: "#fff", border: "1px solid #ddd" }}
                        />
                    ))}
                    {(p.row.tags || []).length > 4 && (
                        <Chip
                            size="small"
                            label={`+${p.row.tags.length - 4}`}
                            sx={{ fontSize: 10, height: 20, background: "#b9cc0340" }}
                        />
                    )}
                </Box>
            ),
        },
        {
            field: "photo_count",
            headerName: "Photos",
            width: 80,
            renderCell: (p) => (
                <Typography sx={{ fontSize: 13, color: "#000" }}>
                    {p.value || 0}
                </Typography>
            ),
        },
        {
            field: "actions",
            headerName: "Actions",
            width: 220,
            sortable: false,
            renderCell: (p) => (
                <Box sx={{ display: "flex", gap: 0.5 }}>
                    <Tooltip title="View / Edit">
                        <IconButton size="small" onClick={() => setDrawerSpotId(p.row.id)}>
                            <VisibilityIcon sx={{ fontSize: 18, color: "#000" }} />
                        </IconButton>
                    </Tooltip>
                    {p.row.status === "pending" && (
                        <>
                            <Tooltip title="Approve">
                                <IconButton size="small" onClick={() => approveSpot(p.row)}>
                                    <CheckCircleIcon sx={{ fontSize: 18, color: "#2e7d32" }} />
                                </IconButton>
                            </Tooltip>
                            <Tooltip title="Reject">
                                <IconButton size="small" onClick={() => rejectSpot(p.row)}>
                                    <CancelIcon sx={{ fontSize: 18, color: "#d33" }} />
                                </IconButton>
                            </Tooltip>
                        </>
                    )}
                    {p.row.status === "approved" && (
                        <Tooltip title="Archive">
                            <IconButton size="small" onClick={() => archiveSpot(p.row)}>
                                <ArchiveIcon sx={{ fontSize: 18, color: "#888" }} />
                            </IconButton>
                        </Tooltip>
                    )}
                </Box>
            ),
        },
    ], []);

    return (
        <Box sx={{ width: "100%", height: "100vh", background: "#B9CC0310", overflow: "hidden" }}>
            <Box sx={{ width: "100%", p: 2 }}>
                <Typography sx={{ fontSize: { xs: "18px", md: "28px" }, fontWeight: 300, color: "#000" }}>
                    Spots Moderation
                </Typography>
            </Box>

            <Box sx={{ px: 2, display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 1 }}>
                <Tabs
                    value={status}
                    onChange={handleTabChange}
                    sx={{
                        "& .MuiTab-root": { textTransform: "none", color: "#000", minHeight: 40 },
                        "& .Mui-selected": { color: "#000", fontWeight: 700 },
                        "& .MuiTabs-indicator": { backgroundColor: "#c6ff00", height: 3 },
                    }}
                >
                    {STATUS_TABS.map((t) => (
                        <Tab
                            key={t.key}
                            value={t.key}
                            label={
                                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                                    {t.label}
                                    <Box
                                        sx={{
                                            minWidth: 22,
                                            height: 20,
                                            px: 0.75,
                                            borderRadius: 10,
                                            background: status === t.key ? "#c6ff00" : "#0001",
                                            color: "#000",
                                            fontSize: 11,
                                            fontWeight: 700,
                                            display: "flex",
                                            alignItems: "center",
                                            justifyContent: "center",
                                        }}
                                    >
                                        {counts[t.key] || 0}
                                    </Box>
                                </Box>
                            }
                        />
                    ))}
                </Tabs>

                <TextField
                    size="small"
                    placeholder="Search name, submitter, or #id"
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    sx={{ width: 260, background: "#fff" }}
                    InputProps={{
                        startAdornment: (
                            <InputAdornment position="start">
                                <SearchIcon sx={{ fontSize: 18, color: "#666" }} />
                            </InputAdornment>
                        ),
                    }}
                />

                <ToggleButtonGroup
                    size="small"
                    value={typeFilter}
                    exclusive
                    onChange={handleTypeChange}
                    sx={{
                        "& .MuiToggleButton-root": { textTransform: "none", color: "#000", borderColor: "#0002" },
                        "& .Mui-selected": { background: "#c6ff00 !important", color: "#000 !important", fontWeight: 700 },
                    }}
                >
                    <ToggleButton value="all">All</ToggleButton>
                    <ToggleButton value="park">Park</ToggleButton>
                    <ToggleButton value="trail">Trail</ToggleButton>
                </ToggleButtonGroup>
            </Box>

            <Box sx={{ width: "100%", height: "calc(100vh - 180px)", p: 2 }}>
                <TableContainer
                    columns={columns}
                    rows={spots}
                    loading={loading}
                    page={page}
                    handlePageChange={setPage}
                    totalData={total}
                    pageSize={pageSize}
                />
            </Box>

            <SpotDetailDrawer
                spotId={drawerSpotId}
                open={drawerSpotId !== null}
                onClose={() => setDrawerSpotId(null)}
                onChanged={() => {
                    setDrawerSpotId(null);
                    fetchList();
                }}
            />
        </Box>
    );
};

export default SpotsModeration;
